interface SignableDocument {
    local?: boolean;
    service?: string;
    uri?: string | null;
    uriExpires?: Date | string | null;
}

/** An existing presigned URL is already a capability. Reuse it only while its
 * actual signed expiry is valid; legacy upload metadata overstates that TTL.
 * This does not verify a signature or mint new access to an object.
 */
function hasUsableSignature(doc: SignableDocument, now: number): boolean {
    if (!doc.uri) return false;
    try {
        const url = new URL(doc.uri);
        if (url.protocol !== 'https:' && url.protocol !== 'http:') return false;
        const date = url.searchParams.get('X-Amz-Date');
        const ttl = Number(url.searchParams.get('X-Amz-Expires'));
        const signature = url.searchParams.get('X-Amz-Signature');
        if (!/^\d{8}T\d{6}Z$/.test(date ?? '') || !/^[a-fA-F0-9]{64}$/.test(signature ?? '') ||
            !Number.isInteger(ttl) || ttl <= 0 || ttl > 604800) return false;
        const issuedAt = Date.parse(`${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}T${date.slice(9, 11)}:${date.slice(11, 13)}:${date.slice(13, 15)}Z`);
        const claimedExpiry = doc.uriExpires ? new Date(doc.uriExpires).getTime() : Infinity;
        return Number.isFinite(issuedAt) && issuedAt <= now && Math.min(issuedAt + ttl * 1000, claimedExpiry) > now + 30_000;
    } catch {
        return false;
    }
}

/** Preserve local drafts and upload-issued URLs while refreshing expired
 * remote documents through the server's authoritative metadata lookup.
 */
export async function refreshDocumentSignatures<T extends SignableDocument>(
    docs: T[], requestSignatures: (documents: T[]) => Promise<T[]>, now = Date.now(),
): Promise<T[]> {
    const result = docs.slice();
    const pending: number[] = [];
    docs.forEach((doc, index) => {
        if (doc.local && doc.service !== 's3') {
            // Keep unsaved local content and its entity prototype intact.
            result[index] = Object.assign(Object.create(Object.getPrototypeOf(doc)), doc, {uri: null, uriExpires: null});
        } else if (!hasUsableSignature(doc, now)) pending.push(index);
    });
    if (pending.length) {
        const signed = await requestSignatures(pending.map(index => docs[index]));
        if (signed.length !== pending.length) throw new Error('Incomplete document-signing response');
        pending.forEach((index, offset) => {
            const doc = docs[index];
            result[index] = Object.assign(Object.create(Object.getPrototypeOf(doc)), doc, {
                uri: signed[offset].uri,
                uriExpires: signed[offset].uriExpires,
            });
        });
    }
    return result;
}
